import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface LocalWishlistItem {
  part_number: string;
  total_quantity: number;
  part_type: string;
  price: number;
  addedAt: number; // timestamp
}

@Injectable({
  providedIn: 'root',
})
export class WishlistStorageService {
  private readonly WISHLIST_KEY = 'local_wishlist';

  constructor() {}

  /**
   * Get all wishlist items from localStorage
   */
  getLocalWishlist(): LocalWishlistItem[] {
    try {
      const data = localStorage.getItem(this.WISHLIST_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading local wishlist:', error);
      return [];
    }
  }

  /**
   * Add item to localStorage wishlist
   */
  addToLocalWishlist(item: Omit<LocalWishlistItem, 'addedAt'>): void {
    try {
      const wishlist = this.getLocalWishlist();

      // Check if item already exists
      const existingIndex = wishlist.findIndex(
        (w) => w.part_number === item.part_number,
      );

      if (existingIndex !== -1) {
        // Update existing item
        wishlist[existingIndex] = {
          ...item,
          addedAt: Date.now(),
        };
      } else {
        // Add new item
        wishlist.push({
          ...item,
          addedAt: Date.now(),
        });
      }

      localStorage.setItem(this.WISHLIST_KEY, JSON.stringify(wishlist));
    } catch (error) {
      console.error('Error adding to local wishlist:', error);
    }
  }

  /**
   * Remove item from localStorage wishlist
   */
  removeFromLocalWishlist(partNumber: string): void {
    try {
      const wishlist = this.getLocalWishlist();
      const filtered = wishlist.filter(
        (item) => item.part_number !== partNumber,
      );
      localStorage.setItem(this.WISHLIST_KEY, JSON.stringify(filtered));
    } catch (error) {
      console.error('Error removing from local wishlist:', error);
    }
  }

  /**
   * Check if item is in localStorage wishlist
   */
  isInLocalWishlist(partNumber: string): boolean {
    const wishlist = this.getLocalWishlist();
    return wishlist.some((item) => item.part_number === partNumber);
  }

  /**
   * Clear all localStorage wishlist items
   */
  clearLocalWishlist(): void {
    try {
      localStorage.removeItem(this.WISHLIST_KEY);
    } catch (error) {
      console.error('Error clearing local wishlist:', error);
    }
  }

  /**
   * Get count of items in localStorage wishlist
   */
  getLocalWishlistCount(): number {
    return this.getLocalWishlist().length;
  }

  /**
   * Get wishlist items formatted for API request
   */
  getWishlistItemsForSync(): any[] {
    const localWishlist = this.getLocalWishlist();
    return localWishlist.map((item) => ({
      part_number: item.part_number,
      total_quantity: item.total_quantity,
      part_type: item.part_type,
      price: item.price,
    }));
  }
}
